import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAmostragem, CadernoLPMAAssinatura } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';

const arquivoParaBase64 = (arquivo: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = reader.result;
			if (typeof resultado !== 'string') {
				reject(new Error('Falha ao converter arquivo para base64.'));
				return;
			}
			const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
			resolve(base64);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo.'));
		reader.readAsDataURL(arquivo);
	});

const MIME_PDF = 'application/pdf';
const MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const validarArquivoPdfOuDocx = (arquivo: File): string | null => {
	const nome = (arquivo.name || '').toLowerCase();
	const okExt = nome.endsWith('.pdf') || nome.endsWith('.docx');
	const okMime =
		arquivo.type === MIME_PDF ||
		arquivo.type === MIME_DOCX ||
		arquivo.type === '' ||
		arquivo.type === 'application/octet-stream';
	if (!okExt) {
		return 'Selecione um arquivo PDF ou DOCX.';
	}
	if (!okMime) {
		return 'Tipo de arquivo não permitido. Use PDF ou DOCX.';
	}
	return null;
};

interface AmostragemModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAAmostragem) => void;
	dadosAmostragem?: CadernoLPMAAmostragem | null;
	cadernoLpmaId?: number | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: CadernoLPMAAmostragem = {
	dataInicioAmostragem: null,
	dataTerminoAmostragem: null,
	responsavelAmostragem: '',
	tipoMaterialAmostradoFolhas: false,
	tipoMaterialAmostradoRaizes: false,
	tipoMaterialAmostradoGraos: false,
	tipoMaterialAmostradoOutros: false,
	outrosTipoMaterialAmostrado: '',
	numeroAMI: '',
	numeroCartaTransferencia: '',
	dataAprovacaoAMI: null,
	quantidadeMaterialAmostrado: '',
	observacoesAmostragem: '',
};

const urlObterAmiPorNumeroAutorizacaoCibio = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?NumeroAutorizacaoCibio=${encodeURIComponent(texto)}`;

const urlObterAmiPorCodigo = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?Codigo=${encodeURIComponent(texto)}`;

const candidatosCodigoAmi = (raw: string): string[] => {
	const t = raw.trim();
	if (!t) return [];
	const out: string[] = [];
	const push = (s: string) => {
		const x = s.trim();
		if (x && !out.includes(x)) out.push(x);
	};
	push(t);
	push(t.replace(/\//g, '.'));
	push(t.replace(/\./g, '/'));
	if (!/^AMI\s/i.test(t)) {
		push(`AMI ${t}`);
		push(`AMI ${t.replace(/\//g, '.')}`);
	}
	return out;
};

const formatarAmiComBarra = (valor: string): string => {
	const digitos = (valor || '').replace(/\D/g, '').slice(0, 7);
	if (digitos.length <= 3) return digitos;
	return `${digitos.slice(0, 3)}/${digitos.slice(3)}`;
};

export default function AmostragemModal({
	show,
	onHide,
	onSubmit,
	dadosAmostragem,
	cadernoLpmaId,
	assinaturasDisponiveis = [],
}: AmostragemModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		watch,
		formState: { errors },
	} = useForm<CadernoLPMAAmostragem>({
		mode: 'all',
		defaultValues,
	});

	const [loadingAmiLink, setLoadingAmiLink] = useState(false);
	const valorNumeroAmi = watch('numeroAMI');
	const [arquivoCartaTransferencia, setArquivoCartaTransferencia] = useState<File | null>(null);
	const [erroArquivoCartaTransferencia, setErroArquivoCartaTransferencia] = useState('');
	const [removerAnexoCartaTransferencia, setRemoverAnexoCartaTransferencia] = useState(false);

	const idAmostragemEdicao = dadosAmostragem?.id ?? (dadosAmostragem as { Id?: number } | undefined)?.Id ?? 0;
	const idCadernoParaDownload = cadernoLpmaId ?? dadosAmostragem?.cadernoLPMAId ?? (dadosAmostragem as { CadernoLPMAId?: number } | undefined)?.CadernoLPMAId ?? 0;

	const baixarAnexoCartaTransferencia = async () => {
		if (!idCadernoParaDownload || !idAmostragemEdicao) {
			return;
		}
		try {
			const resposta = await api.get(
				`api/v1/cadernoLPMA/cadernoLPMA/${idCadernoParaDownload}/amostragens/${idAmostragemEdicao}/carta-transferencia`,
				{ responseType: 'blob' },
			);
			const nome = dadosAmostragem?.cartaTransferenciaNomeArquivo ?? `carta-transferencia-${idAmostragemEdicao}`;
			const contentType = dadosAmostragem?.cartaTransferenciaContentType ?? 'application/octet-stream';
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Erro ao baixar anexo:', error);
		}
	};

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		if (show) {
			const carregarAssinatura = async () => {
				try {
					const token = localStorage.getItem('token');
					if (token) {
						const decoded: any = JSON.parse(atob(token.split('.')[1]));
						const username = decoded.unique_name || '';
						
						const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
						if (data && data.assinaturaBase64) {
							setAssinaturaUsuario(data.assinaturaBase64);
						}
					}
				} catch (error) {
					// Usuário não tem assinatura cadastrada
				}
			};
			
			carregarAssinatura();
			
			// Carregar assinatura salva se existir
			if (dadosAmostragem?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosAmostragem.assinaturaEnvolvidosBase64);
			} else {
				// Limpar se não houver assinatura salva
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosAmostragem?.nomesEnvolvidosAdicionaisJson));
		} else {
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosAmostragem?.assinaturaEnvolvidosBase64, dadosAmostragem?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
			setArquivoCartaTransferencia(null);
			setErroArquivoCartaTransferencia('');
			setRemoverAnexoCartaTransferencia(false);
		} else if (dadosAmostragem) {
			setArquivoCartaTransferencia(null);
			setErroArquivoCartaTransferencia('');
			setRemoverAnexoCartaTransferencia(false);
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			reset({
				...dadosAmostragem,
				dataInicioAmostragem: parseDate(dadosAmostragem.dataInicioAmostragem),
				dataTerminoAmostragem: parseDate(dadosAmostragem.dataTerminoAmostragem),
				dataAprovacaoAMI: parseDate(dadosAmostragem.dataAprovacaoAMI),
			});
		}
	}, [show, dadosAmostragem, reset]);

	const tentarObterAmiPorIdNumerico = async (raw: string): Promise<number | null> => {
		const t = raw.trim();
		if (!/^\d+$/.test(t)) return null;
		const id = parseInt(t, 10);
		if (!Number.isFinite(id) || id <= 0) return null;
		try {
			const { data, status } = await api.get<{ id?: number }>(`api/v1/autorizacoes-cibio/MovimentacaoInterna/${id}`);
			if (status === 200 && typeof data?.id === 'number') return data.id;
		} catch {
		}
		return null;
	};

	const abrirAmiDigitada = async (valorBruto: string) => {
		const valor = (valorBruto || '').trim();
		if (!valor) return;
		setLoadingAmiLink(true);
		try {
			let id = await tentarObterAmiPorIdNumerico(valor);
			if (!id) {
				try {
					const { data } = await api.get(urlObterAmiPorNumeroAutorizacaoCibio(valor));
					id = Array.isArray(data) ? data[0]?.id : data?.id;
				} catch {
				}
			}
			if (!id) {
				for (const candidato of candidatosCodigoAmi(valor)) {
					try {
						const { data } = await api.get(urlObterAmiPorCodigo(candidato));
						id = Array.isArray(data) ? data[0]?.id : data?.id;
						if (id) break;
					} catch {
					}
				}
			}
			if (id) {
				window.open(`/autorizacoes/visualizar/ami?id=${id}`, '_blank');
			}
		} finally {
			setLoadingAmiLink(false);
		}
	};

	const handleSubmitInterno = async (dados: CadernoLPMAAmostragem) => {
		if (erroArquivoCartaTransferencia) {
			return;
		}

		const dadosComAssinatura: CadernoLPMAAmostragem = {
			...dados,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};

		if (removerAnexoCartaTransferencia) {
			dadosComAssinatura.removerCartaTransferenciaAnexo = true;
		} else if (arquivoCartaTransferencia) {
			dadosComAssinatura.cartaTransferenciaConteudoBase64 = await arquivoParaBase64(arquivoCartaTransferencia);
			dadosComAssinatura.cartaTransferenciaNomeArquivo = arquivoCartaTransferencia.name;
			dadosComAssinatura.cartaTransferenciaContentType =
				arquivoCartaTransferencia.type || MIME_DOCX;
		}

		onSubmit(dadosComAssinatura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.AMOSTRAGENS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos e das datas inicial/final de realização da atividade</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>


				<Row className="row-custom">
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataInicioAmostragem"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataInicioAmostragem && <span className="error-field">{errors.dataInicioAmostragem.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataTerminoAmostragem"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataTerminoAmostragem && <span className="error-field">{errors.dataTerminoAmostragem.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Amostragem</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de material GM amostrado</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoFolhas" label="Folhas" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoRaizes" label="Raízes" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoGraos" label="Grãos / sementes" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoOutros" label="Outros (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosTipoMaterialAmostrado"
							placeholder="Descreva o material quando a opção 'Outros' for selecionada"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Número da AMI</Form.Label>
							<Controller
								control={control}
								name="numeroAMI"
								render={({ onChange, value }) => (
									<Form.Control
										value={value || ''}
										placeholder="Informe o número da AMI"
										onChange={(e) => onChange(formatarAmiComBarra(e.target.value))}
									/>
								)}
							/>
							{(valorNumeroAmi || '').trim() ? (
								<button
									type="button"
									className="btn btn-link p-0 mt-1"
									disabled={loadingAmiLink}
									onClick={() => void abrirAmiDigitada(valorNumeroAmi || '')}
								>
									{loadingAmiLink ? 'Buscando AMI...' : `Visualizar AMI ${(valorNumeroAmi || '').trim()}`}
								</button>
							) : null}
							{errors.numeroAMI && <span className="error-field">{errors.numeroAMI.message}</span>}
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Data de aprovação</Form.Label>
							<Controller
								control={control}
								name="dataAprovacaoAMI"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataAprovacaoAMI && <span className="error-field">{errors.dataAprovacaoAMI.message}</span>}
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Unidade / gramas / quilo</Form.Label>
							<Form.Control
								ref={register}
								name="quantidadeMaterialAmostrado"
								placeholder="Ex.: 500 g (folhas)"
								maxLength={150}
							/>
							{errors.quantidadeMaterialAmostrado && <span className="error-field">{errors.quantidadeMaterialAmostrado.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>N.&ordm; da Carta de Transferência</Form.Label>
						<Form.Control
							ref={register}
							name="numeroCartaTransferencia"
							placeholder="Informe o número da carta de transferência"
							maxLength={100}
							style={{ maxWidth: 320 }}
						/>
						{errors.numeroCartaTransferencia && (
							<span className="error-field">{errors.numeroCartaTransferencia.message}</span>
						)}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Carta de Transferência (PDF ou DOCX)</Form.Label>
						<div style={{ maxWidth: 520 }}>
							<Form.Control
								type="file"
								accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								onChange={(e) => {
									const input = e.target as HTMLInputElement;
									const f = input.files?.[0];
									setErroArquivoCartaTransferencia('');
									setRemoverAnexoCartaTransferencia(false);
									if (!f) {
										setArquivoCartaTransferencia(null);
										return;
									}
									const err = validarArquivoPdfOuDocx(f);
									if (err) {
										setErroArquivoCartaTransferencia(err);
										setArquivoCartaTransferencia(null);
										input.value = '';
										return;
									}
									setArquivoCartaTransferencia(f);
								}}
							/>
							{erroArquivoCartaTransferencia ? (
								<small className="text-danger d-block mt-1">{erroArquivoCartaTransferencia}</small>
							) : null}
							{arquivoCartaTransferencia ? (
								<small className="d-block text-muted mt-1">Selecionado: {arquivoCartaTransferencia.name}</small>
							) : null}
							{!arquivoCartaTransferencia &&
							dadosAmostragem?.cartaTransferenciaNomeArquivo &&
							!removerAnexoCartaTransferencia ? (
								<div className="mt-2">
									<small className="text-muted">
										Arquivo atual: {dadosAmostragem.cartaTransferenciaNomeArquivo}
									</small>
									{idCadernoParaDownload > 0 && idAmostragemEdicao > 0 ? (
										<>
											{' '}
											<Button
												type="button"
												contained={false}
												title="Baixar"
												onClick={() => void baixarAnexoCartaTransferencia()}
											/>
										</>
									) : null}
									{' '}
									<Button
										type="button"
										contained={false}
										title="Remover anexo"
										onClick={() => {
											setRemoverAnexoCartaTransferencia(true);
											setArquivoCartaTransferencia(null);
										}}
									/>
								</div>
							) : null}
							{removerAnexoCartaTransferencia ? (
								<small className="text-warning d-block mt-1">A carta será removida ao salvar.</small>
							) : null}
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesAmostragem"
							placeholder="Registre informações complementares sobre a atividade de amostragem"
							maxLength={500}
						/>
						{errors.observacoesAmostragem && <span className="error-field">{errors.observacoesAmostragem.message}</span>}
					</Form.Group>
				</Form.Row>

				<p className="mt-2">
					<small>
						Para registrar outra atividade, insira este formulário e abra novamente a amostragem pelo botão correspondente.
					</small>
				</p>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

